import './CreateEditDialog.scss';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../../context/ToastContext';
import useAxios from '../../../../../../hooks/useAxios';
import useMediaQuery from '../../../../../../hooks/useMediaQuery';
import { WithData } from '../../../../../../types/api';
import { FormDataHub, HubType } from '../../../../../../types/api/hubs';
import {
  errorToast,
  successToast,
} from '../../../../../../utils/helpers/primereact';
import { toApiData } from './CreateEditDialog.functions';
import FormContainer from './FormContainer';

type Props = {
  visible: boolean;
  onHide: () => void;
  data: WithData<HubType> | undefined;
  isLoading: boolean;
  isEditDialog: boolean;
  reloadHubs: () => void;
};

function CreateEditDialog({
  visible,
  onHide,
  isEditDialog,
  data,
  reloadHubs,
}: Props) {
  const { t } = useTranslation();
  const { toastRef } = useContext(ToastContext);

  const { reload: formSubmitRequest, isLoading: isFormSubmitRequestLoading } =
    useAxios();

  function handleHubCreate(name: string, isSuccess: boolean) {
    if (isSuccess) {
      successToast(
        toastRef,
        t('Success'),
        t('Warehouse {{name}} has been added successfully', { name: name })
      );
    } else {
      errorToast(
        toastRef,
        t('Error'),
        t('An error occured while adding {{name}}.', { name: name })
      );
    }
    reloadHubs();
    onHide();
  }

  function handleHubEdit(name: string, isSuccess: boolean) {
    if (isSuccess) {
      successToast(
        toastRef,
        t('Success'),
        t('Warehouse {{name}} has been edited successfully', { name: name })
      );
    } else {
      errorToast(
        toastRef,
        t('Error'),
        t('An error occured while editing {{name}}.', { name: name })
      );
    }

    reloadHubs();
    onHide();
  }

  function handleSubmitForm(values: FormDataHub) {
    if (isEditDialog) {
      formSubmitRequest({
        url: `/hubs/${data?.data?.id}`,
        payload: toApiData(values),
        method: 'POST',
        successCallback: () => {
          handleHubEdit(values.name, true);
        },
        errorCallback: (err: any) => {
          console.log('err ', err);
          // const formErrors = formik_handleNewErrorFormatUponSubmission(
          //   err?.response,
          //   createEditDialogFormRef,
          //   allowedKeys,
          //   allowedValues
          // );
          if (err) {
            errorToast(toastRef, t('Error'), t('An error occured ', err));
            // setIsNameUsed(true);
            return;
          } else {
            handleHubEdit(values.name, false);
          }
        },
      });
    } else {
      formSubmitRequest({
        url: '/hubs',
        payload: toApiData(values),
        method: 'POST',
        successCallback: () => {
          handleHubCreate(values.name, true);
        },
        errorCallback: (err: any) => {
          console.log('err ', err);
          // const formErrors = formik_handleNewErrorFormatUponSubmission(
          //   err?.response,
          //   createEditDialogFormRef,
          //   allowedKeys,
          //   allowedValues
          // );
          if (err) {
            errorToast(toastRef, t('Error'), t('An error occured ', err));
            // setIsNameUsed(true);
            return;
          } else {
            handleHubCreate(values.name, false);
          }
        },
      });
    }
  }

  const dialogHeader = isEditDialog ? data?.data.name : t('Add new warehouse');

  const dialogFooter = (
    <div>
      <Button
        type="button"
        label={t('Cancel')}
        className="p-button-secondary p-button-text"
        onClick={() => onHide()}
      />

      <Button
        type="submit"
        form="hub-create-edit-form"
        label={
          isFormSubmitRequestLoading
            ? t('Saving...')
            : isEditDialog
            ? t('Save changes')
            : t('Create')
        }
        disabled={isFormSubmitRequestLoading}
        className="p-button-primary"
        data-cy="submit"
      />
    </div>
  );

  const isOnMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={dialogHeader}
      footer={dialogFooter}
      blockScroll
      resizable={false}
      maximizable
      maximized={isOnMobile}
      className="hubs-create-edit-dialog"
    >
      <FormContainer
        isEditDialog={isEditDialog}
        handleSubmitForm={handleSubmitForm}
        data={data}
      />
    </Dialog>
  );
}

export default CreateEditDialog;
